/**
 * useMessageRoles — 消息气泡渲染（roles / renderThreePartContent / highlightCitations）
 * 从 OpsMateContext 中抽取
 */
import React, { Component, useCallback, useMemo, useRef, useState } from "react";
import { Actions } from "@ant-design/x";
import { App, Button, Divider, Flex, Popover, Tag, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import {
  DownloadOutlined,
  LoadingOutlined,
  ReloadOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import XMarkdown from "@ant-design/x-markdown";

import AiLoading from "@/hooks/opsmate/AiLoading";
import ProgressPanel from "@/pages/opsmate/components/CustomerSuggestion/ProgressPanel";
import {
  getDocumentGuid,
  getDocumentName,
} from "@/pages/opsmate/components/DocumentList/DocumentList.helpers";
import { makeArtifactImgComponents } from "@/utils/warehouseArtifactComponents";
import { resolveDownloadUrl } from "@/utils/apiUrl";

import CopyIcon from "@/assets/opsmate/copy.svg";
import GoodResponseIcon from "@/assets/opsmate/GoodResponse.svg";
import BadResponseIcon from "@/assets/opsmate/BadResponse.svg";
import GoodResponseActiveIcon from "@/assets/opsmate/Good Response1.svg";
import BadResponseActiveIcon from "@/assets/opsmate/Bad Response1.svg";
// AI Reasoning（Rationale）入口已隐藏，图标暂不引用
// import AIReasoningIcon from "@/assets/opsmate/AIResoning.svg";
import TranslationedIcon from "@/assets/opsmate/translate.svg";
import TranslationIcon from "@/assets/opsmate/translate1.svg";

// ── 引用角标统一样式（与底部 References 保持一致）──
const CITATION_TAG_STYLE: React.CSSProperties = {
  color: "#61666b",
  background: "#ebeef2",
  borderRadius: 999,
  minWidth: 14,
  height: 14,
  padding: "0 0",
  fontSize: 8,
  lineHeight: "14px",
  marginRight: 3,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const STREAMING_CURSOR_SENTINEL = "__OPS_MATE_STREAMING_CURSOR__";

class MarkdownErrorBoundary extends Component<
  { fallback: string; children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: { fallback: string }) {
    if (prevProps.fallback !== this.props.fallback && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{this.props.fallback}</pre>;
    }
    return this.props.children;
  }
}

// ── marked 扩展：
//   把 `[N]` 和 `<pike_rag_cite>N</pike_rag_cite>` 两种引用标记
//   统一在 Markdown 解析阶段转为 `<pike-cite data-num="N"></pike-cite>`，
//   再交给 XMarkdown 的 components 映射为可交互的 Popover + Tag。
//   这样可以避免在纯字符串层面做替换时误伤代码块中的 `[N]` 字面量。
const citationMarkedConfig: any = {
  breaks: true,
  gfm: true,
  extensions: [
    {
      name: "pikeCitation",
      level: "inline",
      start(src: string) {
        const m = src.match(/\[\d+(?:-\d+)?\]|<\|?pike_rag_cite\|?>/i);
        return m ? m.index : undefined;
      },
      tokenizer(src: string) {
        const m =
          /^\[(\d+(?:-\d+)?)\]|^<\|?pike_rag_cite\|?>\s*(\d+(?:-\d+)?)\s*<\|?\/pike_rag_cite\|?>/i.exec(
            src,
          );
        if (m) {
          return {
            type: "pikeCitation",
            raw: m[0],
            num: m[1] ?? m[2] ?? "",
          };
        }
        return undefined;
      },
      renderer(token: any) {
        return `<pike-cite data-num="${token.num}"></pike-cite>`;
      },
    },
    {
      name: "streamingCursor",
      level: "inline",
      start(src: string) {
        const index = src.indexOf(STREAMING_CURSOR_SENTINEL);
        return index >= 0 ? index : undefined;
      },
      tokenizer(src: string) {
        if (!src.startsWith(STREAMING_CURSOR_SENTINEL)) return undefined;
        return {
          type: "streamingCursor",
          raw: STREAMING_CURSOR_SENTINEL,
        };
      },
      renderer() {
        return "<ai-streaming-cursor-inline></ai-streaming-cursor-inline>";
      },
    },
  ],
};

interface UseMessageRolesParams {
  itemsRef: React.MutableRefObject<any[]>;
  setDocsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  setHighlightedDocGuid: React.Dispatch<React.SetStateAction<string>>;
  setAIReasoningVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setAIReasoning: React.Dispatch<React.SetStateAction<string>>;
  handleTranslate: (itemKey: string, sessionLogId?: string) => Promise<void>;
  handleFeedbackChange: (
    itemKey: string,
    type: "like" | "dislike" | "default",
    sessionLogId?: string,
  ) => Promise<void>;
  regenerateMessage: (aiMessageKey: string) => Promise<void>;
  setDislikeConfirmTarget: React.Dispatch<React.SetStateAction<any>>;
  setDislikeConfirmVisible: React.Dispatch<React.SetStateAction<boolean>>;
  /** 审计/只读场景下隐藏「翻译 / 点赞 / 点踩」按钮（仅保留复制） */
  hideTranslateAndFeedback?: boolean;
}

export function useMessageRoles({
  itemsRef,
  setDocsCollapsed,
  setHighlightedDocGuid,
  setAIReasoningVisible,
  setAIReasoning,
  handleTranslate,
  handleFeedbackChange,
  regenerateMessage,
  setDislikeConfirmTarget,
  setDislikeConfirmVisible,
  hideTranslateAndFeedback = false,
}: UseMessageRolesParams) {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const messageRef = useRef(message);
  messageRef.current = message;
  const [copyingMap, setCopyingMap] = useState<Record<string, boolean>>({});

  // ── 工具函数 ──
  const canShowDownload = useCallback(
    (
      doc:
        | string
        | { name: string; citations?: string | string[]; canDownload?: boolean; fileUrl?: string },
    ): boolean => {
      if (typeof doc === "object" && doc.canDownload === false) return false;
      return true;
    },
    [],
  );

  const getDocUrl = useCallback(
    (
      doc:
        | string
        | { name: string; citations?: string | string[]; canDownload?: boolean; fileUrl?: string },
    ): string | undefined => {
      if (typeof doc === "string") return undefined;
      return doc.fileUrl;
    },
    [],
  );

  // ── 高亮引用标记 [N] ──
  // 角标编号一律按 SSE 流式内容里的原始数字 N 渲染，不做任何映射/去重：
  //   - 角标显示文字 = 原文 N
  //   - 点击行为     = 跳转到 documents[N-1]（若存在），不存在时退化为不可点击 Tag
  const highlightCitations = useCallback(
    (
      content: string,
      messageDocuments?: Array<
        | string
        | { name: string; citations?: string | string[]; canDownload?: boolean; fileUrl?: string }
      >,
    ): React.ReactNode => {
      if (typeof content !== "string") return content;

      const citationPattern =
        /\[(\d+(?:-\d+)?)\]|<\|?pike_rag_cite\|?>\s*(\d+(?:-\d+)?)\s*<\|?\/pike_rag_cite\|?>/gi;

      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let keyCounter = 0;
      const docsLength = Array.isArray(messageDocuments) ? messageDocuments.length : 0;

      const numMatches: Array<{ start: number; end: number; num: string }> = [];
      let numMatch: RegExpExecArray | null = citationPattern.exec(content);
      while (numMatch !== null) {
        const numText = numMatch[1] ?? numMatch[2] ?? "";
        if (!numText) {
          numMatch = citationPattern.exec(content);
          continue;
        }
        numMatches.push({
          start: numMatch.index,
          end: numMatch.index + numMatch[0].length,
          num: numText,
        });
        numMatch = citationPattern.exec(content);
      }

      numMatches.forEach((match) => {
        const citationNum = match.num.includes("-")
          ? parseInt(match.num.split("-")[0], 10)
          : parseInt(match.num, 10);
        const inBetween = content.substring(lastIndex, match.start);
        const displayLabel = String(citationNum);

        if (inBetween) {
          parts.push(<span key={`text-${keyCounter++}`}>{inBetween}</span>);
        }

        const docIndex =
          Number.isFinite(citationNum) && citationNum > 0 && citationNum - 1 < docsLength
            ? citationNum - 1
            : -1;

        if (docIndex < 0) {
          parts.push(
            <Tag key={`ref-raw-${keyCounter++}`} variant="filled" style={CITATION_TAG_STYLE}>
              {displayLabel}
            </Tag>,
          );
          lastIndex = match.end;
          return;
        }

        const doc = messageDocuments?.[docIndex];
        const docName = typeof doc === "string" ? doc : (doc?.name ?? "未知文档");
        const citationsRaw =
          typeof doc === "object" && doc && (doc as any).citations ? (doc as any).citations : "";
        const citationsText = Array.isArray(citationsRaw)
          ? citationsRaw.join("\n\n---\n\n")
          : String(citationsRaw ?? "");
        const docArtifactId =
          typeof doc === "object" && doc && (doc as any).id ? String((doc as any).id) : "";

        parts.push(
          <Popover
            key={`ref-popover-${keyCounter++}`}
            trigger="hover"
            title={
              <Flex align="center" justify="space-between">
                <>{docName}</>
                {canShowDownload(doc as any) ? (
                  <Button
                    icon={<DownloadOutlined />}
                    color="primary"
                    variant="link"
                    size="large"
                    style={{ fontSize: 20 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = getDocUrl(doc as any);
                      if (!url) {
                        messageRef.current.warning(
                          "The download link is temporarily unavailable. Please try again later.",
                        );
                        return;
                      }
                      window.open(resolveDownloadUrl(url), "_blank", "noopener,noreferrer");
                    }}
                  />
                ) : null}
              </Flex>
            }
            content={
              <>
                <Divider style={{ marginBlock: 7 }} />
                <div style={{ width: 680, height: "auto", maxHeight: 390, overflowY: "auto" }}>
                  <XMarkdown
                    content={citationsText}
                    components={makeArtifactImgComponents(docArtifactId)}
                  />
                </div>
              </>
            }
          >
            <Tag
              variant="filled"
              style={{ ...CITATION_TAG_STYLE, cursor: "pointer" }}
              onClick={() => {
                setDocsCollapsed(false);
                const guid = getDocumentGuid(doc as any) || getDocumentName(doc as any);
                setHighlightedDocGuid(guid);
                setTimeout(() => setHighlightedDocGuid(""), 3000);
              }}
            >
              {displayLabel}
            </Tag>
          </Popover>,
        );

        lastIndex = match.end;
      });

      if (lastIndex < content.length) {
        parts.push(<span key={`text-${keyCounter++}`}>{content.substring(lastIndex)}</span>);
      }

      return parts.length > 0 ? parts : content;
    },
    [canShowDownload, getDocUrl, setDocsCollapsed, setHighlightedDocGuid],
  );

  // ── Markdown 渲染 Answer 正文（支持 Markdown + 引用角标）──
  //   - 使用 XMarkdown 渲染 markdown（标题 / 列表 / 代码块 / 表格 / 加粗 / 链接等）
  //   - 通过 citationMarkedConfig 把 `[N]` / `<pike_rag_cite>` 统一转为 `<pike-cite>` 自定义标签
  //   - 在 components 中把 `<pike-cite>` 替换为 Popover + Tag，复用和底部 References 一致的交互
  const renderMarkdownAnswer = useCallback(
    (
      content: string,
      docs: Array<
        | string
        | {
            id?: string;
            name: string;
            citations?: string | string[];
            canDownload?: boolean;
            fileUrl?: string;
            segmentNums?: number[];
          }
      >,
      options?: { streamingCursor?: boolean; references?: any[] },
    ): React.ReactNode => {
      const text = typeof content === "string" ? content : "";
      if (!text) return null;
      // sentinel 前补一个空格，避免它"粘"住前一段尾巴的换行——
      // 否则当 text 以 `\n\n` 结尾时，marked 会把 sentinel 当作下一段 inline
      // 内容，inline 切分有可能吞掉第二个 `\n`。
      const contentWithCursor = options?.streamingCursor
        ? `${text} ${STREAMING_CURSOR_SENTINEL}`
        : text;

      const refs = Array.isArray(options?.references) ? options!.references! : [];
      const CitationTag: React.FC<any> = (p) => {
        const numText = String(p["data-num"] ?? "");
        if (!numText) return null;
        const citationNum = numText.includes("-")
          ? parseInt(numText.split("-")[0], 10)
          : parseInt(numText, 10);
        if (!Number.isFinite(citationNum)) return null;

        // [N] 表示 references[N-1]（片段级）。popup 内容只认这条 reference 的 segment 字段，
        // 不依赖文档去重列表是否命中——这样即使 documentId 为空（如 mvr-pharma）也能正常悬浮显示。
        const ref = citationNum > 0 && citationNum - 1 < refs.length ? refs[citationNum - 1] : null;

        // 没有对应 reference 时，渲染不可悬浮的原始编号角标
        if (!ref) {
          return (
            <Tag variant="filled" style={CITATION_TAG_STYLE} data-pike-citation="true">
              {String(citationNum)}
            </Tag>
          );
        }

        // 该角标对应的具体片段内容（仅当前 [N] 对应的那一条 segment）
        const refSegmentRaw = String(ref?.references_segment ?? "");
        const marker = "Content: ";
        const markerIdx = refSegmentRaw.indexOf(marker);
        const citationsText =
          markerIdx >= 0 ? refSegmentRaw.slice(markerIdx + marker.length) : refSegmentRaw;
        // 片段里的图片走后端鉴权中转：documentId 取该条引用的 owner 文档 ID
        const refArtifactDocId = String(ref?.references_document_id ?? ref?.document_id ?? "");

        // 标题与下载/跳转走文档去重列表：命中则用文档信息，未命中退化为 reference 自带的文档名
        const docIndex = refArtifactDocId
          ? docs.findIndex(
              (d: any) =>
                typeof d === "object" && d?.id && String(d.id) === String(refArtifactDocId),
            )
          : -1;
        const doc = docIndex >= 0 ? docs[docIndex] : null;
        const docName =
          (typeof doc === "string" ? doc : doc?.name) ||
          String(ref?.references_document_name ?? ref?.documentName ?? "") ||
          "未知文档";

        return (
          <Popover
            trigger="hover"
            title={
              <Flex align="center" justify="space-between">
                <>{docName}</>
                {doc && canShowDownload(doc as any) ? (
                  <Button
                    icon={<DownloadOutlined />}
                    color="primary"
                    variant="link"
                    size="large"
                    style={{ fontSize: 20 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = getDocUrl(doc as any);
                      if (!url) {
                        messageRef.current.warning(
                          "The download link is temporarily unavailable. Please try again later.",
                        );
                        return;
                      }
                      window.open(resolveDownloadUrl(url), "_blank", "noopener,noreferrer");
                    }}
                  />
                ) : null}
              </Flex>
            }
            content={
              <>
                <Divider style={{ marginBlock: 7 }} />
                <div style={{ width: 680, height: "auto", maxHeight: 390, overflowY: "auto" }}>
                  <XMarkdown
                    content={citationsText}
                    components={makeArtifactImgComponents(refArtifactDocId)}
                  />
                </div>
              </>
            }
          >
            <Tag
              variant="filled"
              style={{ ...CITATION_TAG_STYLE, cursor: "pointer" }}
              data-pike-citation="true"
              onClick={() => {
                setDocsCollapsed(false);
                const guid = doc ? getDocumentGuid(doc as any) || getDocumentName(doc as any) : "";
                if (guid) setHighlightedDocGuid(guid);
                setTimeout(() => setHighlightedDocGuid(""), 3000);
              }}
            >
              {String(citationNum)}
            </Tag>
          </Popover>
        );
      };

      const StreamingCursorInline = () => (
        <span className="ai-streaming-cursor" aria-hidden>
          ▍
        </span>
      );

      return (
        <MarkdownErrorBoundary fallback={text}>
          <XMarkdown
            content={contentWithCursor}
            config={citationMarkedConfig}
            components={{
              "pike-cite": CitationTag,
              "ai-streaming-cursor-inline": StreamingCursorInline,
            }}
          />
        </MarkdownErrorBoundary>
      );
    },
    [canShowDownload, getDocUrl, setDocsCollapsed, setHighlightedDocGuid],
  );

  // ── Answer 正文容器样式（配合 Markdown 渲染去掉内部 margin 顶部空白） ──
  const answerBodyStyle: React.CSSProperties = useMemo(
    () => ({
      margin: 0,
      lineHeight: 1.6,
      color: "rgba(0, 0, 0, 0.85)",
      fontSize: 14,
      wordBreak: "break-word",
    }),
    [],
  );

  // ── 渲染三段式内容（Summary / Answer / 引用的文档名称） ──
  const renderThreePartContent = useCallback(
    (bubbleMessage: any): React.ReactNode => {
      const summary = bubbleMessage?.summary ?? "";
      const answerContent = typeof bubbleMessage?.content === "string" ? bubbleMessage.content : "";
      const docs: any[] =
        Array.isArray(bubbleMessage?.documents) && bubbleMessage.documents.length > 0
          ? bubbleMessage.documents
          : [];
      const references: any[] = Array.isArray(bubbleMessage?.references)
        ? bubbleMessage.references
        : [];
      const { Title, Paragraph } = Typography;
      const sectionTitleStyle = {
        margin: "0 0 0.5em 0",
        fontWeight: 600,
        fontSize: 16,
        color: "rgba(0, 0, 0, 0.85)",
        lineHeight: 1.4,
      };
      const dividerStyle = {
        height: 1,
        backgroundColor: "rgba(0, 0, 0, 0.12)",
        margin: "12px 0",
        width: "100%" as const,
      };
      const parts: React.ReactNode[] = [];

      // 0. 推理 / 检索 过程（若存在则展示，默认折叠，可展开查看）
      const progressSteps: any[] = Array.isArray(bubbleMessage?.progress)
        ? bubbleMessage.progress
        : [];
      if (progressSteps.length > 0) {
        parts.push(
          <div key="progress-section" style={{ marginBottom: 16 }}>
            <ProgressPanel steps={progressSteps} streaming={false} />
          </div>,
        );
      }

      const hasTranslation = bubbleMessage?.extraInfo && "isTranslated" in bubbleMessage.extraInfo;
      const isTranslated = hasTranslation ? bubbleMessage.extraInfo.isTranslated : false;
      const translatedSummary = hasTranslation ? bubbleMessage.extraInfo.translatedSummary : "";
      const translatedText = hasTranslation ? bubbleMessage.extraInfo.translatedText : "";

      const hasTranslatedContent =
        (translatedSummary && translatedSummary.length > 0) ||
        (translatedText && translatedText.length > 0);
      const dashedDividerStyle = {
        borderTop: "1px dashed rgba(0, 0, 0, 0.25)",
        margin: "12px 0",
        width: "100%" as const,
      };

      if (summary && summary.length > 0) {
        parts.push(
          <div
            key="summary-section"
            id={`ai-summary-${bubbleMessage?.key}`}
            style={{ marginBottom: 12 }}
          >
            <Title level={5} style={sectionTitleStyle}>
              Summary
            </Title>
            <div className="opsmate-answer-markdown" style={answerBodyStyle}>
              {renderMarkdownAnswer(summary || "", docs, { references })}
            </div>
          </div>,
        );
      }

      parts.push(
        <div
          key="answer-section"
          style={{ marginBottom: 12 }}
          data-copy-answer={String(bubbleMessage?.key ?? "")}
        >
          <Title level={5} style={sectionTitleStyle}>
            Answer
          </Title>
          <div className="opsmate-answer-markdown" style={answerBodyStyle}>
            {renderMarkdownAnswer(answerContent || "", docs, { references })}
          </div>
        </div>,
      );

      if (hasTranslatedContent) {
        parts.push(<div key="divider-translation" style={dashedDividerStyle} />);
        if (translatedSummary && translatedSummary.length > 0) {
          parts.push(
            <div key="translated-summary" style={{ marginBottom: 12 }}>
              <Title level={5} style={sectionTitleStyle}>
                Summary
              </Title>
              <div className="opsmate-answer-markdown" style={answerBodyStyle}>
                {renderMarkdownAnswer(translatedSummary, docs, { references })}
              </div>
            </div>,
          );
        }
        if (translatedText && translatedText.length > 0) {
          parts.push(
            <div
              key="translated-answer"
              style={{ marginBottom: 12 }}
              data-copy-answer={String(bubbleMessage?.key ?? "")}
            >
              <Title level={5} style={sectionTitleStyle}>
                Answer
              </Title>
              <div className="opsmate-answer-markdown" style={answerBodyStyle}>
                {renderMarkdownAnswer(translatedText, docs, { references })}
              </div>
            </div>,
          );
        }
      }
      parts.push(<div key="divider-2" style={dividerStyle} />);

      if (hasTranslation && isTranslated && !translatedSummary && !translatedText) {
        parts.push(<LoadingOutlined key="translate-loading" />);
      }

      parts.push(
        <div key="sources-section" style={{ marginTop: 12 }}>
          <Title level={5} style={{ ...sectionTitleStyle, fontSize: 13 }}>
            References
          </Title>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {docs.length === 0 ? (
              <Paragraph
                type="secondary"
                style={{ margin: 0, fontSize: 13, color: "rgba(0,0,0,0.45)" }}
              >
                No referenced documents
              </Paragraph>
            ) : (
              docs.map((doc: any, idx: number) => {
                const docName = typeof doc === "string" ? doc : doc.name;
                const citations = typeof doc === "object" && doc.citations ? doc.citations : "";
                // 文档级片段里的图片走后端鉴权中转：documentId 取该文档的 owner 文档 ID
                const docArtifactId = typeof doc === "object" && doc.id ? String(doc.id) : "";
                const segNums: number[] =
                  typeof doc === "object" && Array.isArray(doc.segmentNums)
                    ? doc.segmentNums
                    : [idx + 1];

                const popoverTitle = (
                  <Flex align="center" justify="space-between">
                    <>{docName}</>
                    {canShowDownload(doc) ? (
                      <Button
                        icon={<DownloadOutlined />}
                        color="primary"
                        variant="link"
                        size="large"
                        style={{ fontSize: 20 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = getDocUrl(doc);
                          if (!url) {
                            messageRef.current.warning(
                              "The download link is temporarily unavailable. Please try again later.",
                            );
                            return;
                          }
                          window.open(resolveDownloadUrl(url), "_blank", "noopener,noreferrer");
                        }}
                      />
                    ) : null}
                  </Flex>
                );
                const popoverContent = (
                  <>
                    <Divider style={{ marginBlock: 7 }} />
                    <div style={{ width: 680, height: "auto", maxHeight: 390, overflowY: "auto" }}>
                      <XMarkdown
                        content={typeof citations === "string" ? citations : ""}
                        components={makeArtifactImgComponents(docArtifactId)}
                      />
                    </div>
                  </>
                );

                return (
                  <div
                    key={idx}
                    style={{
                      color: "rgba(0, 0, 0, 0.65)",
                      fontSize: 13,
                      lineHeight: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        background: "rgba(0, 0, 0, 0.45)",
                        flex: "0 0 auto",
                      }}
                    />
                    <Popover trigger="hover" title={popoverTitle} content={popoverContent}>
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setDocsCollapsed(false);
                          const guid = getDocumentGuid(doc as any) || getDocumentName(doc as any);
                          setHighlightedDocGuid(guid);
                          setTimeout(() => setHighlightedDocGuid(""), 3000);
                        }}
                      >
                        {docName}
                      </span>
                    </Popover>
                    <span style={{ color: "rgba(0,0,0,0.35)" }}></span>
                    {segNums.map((n) => {
                      const segRaw =
                        n > 0 && n - 1 < references.length
                          ? String(references[n - 1]?.references_segment ?? "")
                          : "";
                      const segMarker = "Content: ";
                      const segMarkerIdx = segRaw.indexOf(segMarker);
                      const segText =
                        segMarkerIdx >= 0 ? segRaw.slice(segMarkerIdx + segMarker.length) : segRaw;
                      // 该 segment 片段里的图片走后端鉴权中转：documentId 取对应引用的 owner 文档 ID
                      const segArtifactId =
                        n > 0 && n - 1 < references.length
                          ? String(
                              references[n - 1]?.references_document_id ??
                                references[n - 1]?.document_id ??
                                "",
                            )
                          : "";
                      return (
                        <Popover
                          key={`seg-${n}`}
                          trigger="hover"
                          title={popoverTitle}
                          content={
                            <>
                              <Divider style={{ marginBlock: 7 }} />
                              <div
                                style={{
                                  width: 680,
                                  height: "auto",
                                  maxHeight: 390,
                                  overflowY: "auto",
                                }}
                              >
                                <XMarkdown
                                  content={segText}
                                  components={makeArtifactImgComponents(segArtifactId)}
                                />
                              </div>
                            </>
                          }
                        >
                          <Tag
                            variant="filled"
                            style={{ ...CITATION_TAG_STYLE, cursor: "pointer" }}
                            onClick={() => {
                              setDocsCollapsed(false);
                              const guid =
                                getDocumentGuid(doc as any) || getDocumentName(doc as any);
                              setHighlightedDocGuid(guid);
                              setTimeout(() => setHighlightedDocGuid(""), 3000);
                            }}
                          >
                            {n}
                          </Tag>
                        </Popover>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </div>,
      );

      return parts.length > 0 ? <div style={{ padding: "16px 0 0 0" }}>{parts}</div> : null;
    },
    [
      renderMarkdownAnswer,
      answerBodyStyle,
      canShowDownload,
      getDocUrl,
      setDocsCollapsed,
      setHighlightedDocGuid,
    ],
  );

  // ── Bubble roles ──
  const roles = useCallback(
    (_setMessage: React.Dispatch<React.SetStateAction<any[]>>): any => ({
      ai: {
        placement: "start" as const,
        className: "ai-message",
        typing: (_: any, { status }: any) =>
          status === "updating" ? { effect: "typing", step: 5, interval: 20 } : false,
        loadingRender: () => <AiLoading />,
        contentRender: (content: any, info: any) => {
          const currentMessage = itemsRef.current.find((item) => item.key === info.key);

          if (
            content ===
              "The knowledge base is currently being updated. Some documents have not yet been fully parsed, so a reliable answer cannot be generated at this time. Please try again later." ||
            content === "Okay, I've stopped responding." ||
            /^\[请求失败\s\d+\]/.test(content) ||
            (!content && currentMessage?.status !== "streaming")
          ) {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  borderRadius: 8,
                  marginBottom: -12,
                  //   border: '1px solid #ffccc7',
                  //   background: '#fff2f0',
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "#434343", fontSize: 13 }}>
                    {content || currentMessage?.execMsg?.message}
                  </span>
                </div>
              </div>
            );
          }
          if (currentMessage?.status === "error") {
            // 错误状态：显示错误卡片，仅最新一条 AI 消息显示重试按钮
            const errorText = "Okay, I've stopped responding. Would you like me to continue?";
            // 判断是否为最后一条 AI 消息
            const lastAiMessage = [...itemsRef.current]
              .reverse()
              .find((item) => item.role === "ai");
            const isLatestAi = lastAiMessage?.key === info.key;
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  borderRadius: 8,
                  marginBottom: -12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "#434343", fontSize: 13 }}>{errorText}</span>
                </div>
                {isLatestAi && (
                  <div>
                    <Button
                      size="small"
                      style={{
                        fontSize: 13,
                        marginBottom: 12,
                      }}
                      icon={<ReloadOutlined />}
                      onClick={() => regenerateMessage(String(info.key ?? ""))}
                    >
                      {t("opsmate.regenerate")}
                    </Button>
                  </div>
                )}
              </div>
            );
          }

          // 流式中：先展示推理/检索过程面板，再渲染正在生成的 Answer 正文
          if (currentMessage?.status === "streaming") {
            const streamingText =
              typeof currentMessage.content === "string"
                ? currentMessage.content
                : typeof content === "string"
                  ? content
                  : "";
            const streamingProgress: any[] = Array.isArray(currentMessage.progress)
              ? currentMessage.progress
              : [];
            const hasStreamingText = streamingText.length > 0;
            const sectionTitleStyle = {
              margin: "0 0 0.5em 0",
              fontWeight: 600,
              fontSize: 16,
              color: "rgba(0, 0, 0, 0.85)",
              lineHeight: 1.4,
            };

            return (
              <div style={{ padding: "16px 0 0 0" }}>
                {streamingProgress.length > 0 || !hasStreamingText ? (
                  <div style={{ marginBottom: 12 }}>
                    <ProgressPanel steps={streamingProgress} streaming defaultOpen />
                  </div>
                ) : null}
                {hasStreamingText ? (
                  <div key="streaming-answer-section" style={{ marginBottom: 12 }}>
                    <Typography.Title level={5} style={sectionTitleStyle}>
                      Answer
                    </Typography.Title>
                    <div
                      className="opsmate-answer-markdown"
                      style={{
                        margin: 0,
                        lineHeight: 1.6,
                        color: "rgba(0, 0, 0, 0.85)",
                        fontSize: 13,
                        wordBreak: "break-word",
                      }}
                    >
                      {renderMarkdownAnswer(streamingText, [], { streamingCursor: true })}
                    </div>
                  </div>
                ) : null}
                {(() => {
                  // 流式途中的 References 预览：随 ```references 逐条解析出的完整记录边构建边显示，
                  // 不等整段 JSON、不等 getdocumentid。完成态会用带下载/悬浮的完整版替换。
                  const streamingDocs: any[] = Array.isArray(currentMessage?.documents)
                    ? currentMessage.documents
                    : [];
                  const building = !!currentMessage?.buildingReferences;
                  if (!building && streamingDocs.length === 0) return null;
                  return (
                    <>
                      {/* 与最终态 References 一致：区块上方加一条分隔横线 */}
                      <div
                        style={{
                          height: 1,
                          backgroundColor: "rgba(0, 0, 0, 0.12)",
                          margin: "12px 0",
                          width: "100%",
                        }}
                      />
                      <div style={{ marginTop: 12 }}>
                        <Typography.Title level={5} style={{ ...sectionTitleStyle, fontSize: 13 }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            References
                            {building ? (
                              <>
                                <LoadingOutlined
                                  style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.45)" }}
                                />
                                <span
                                  style={{
                                    fontSize: 13,
                                    color: "rgba(0, 0, 0, 0.45)",
                                    fontWeight: "normal",
                                  }}
                                >
                                  Extracting referenced document...
                                </span>
                              </>
                            ) : null}
                          </span>
                        </Typography.Title>
                        {streamingDocs.length === 0 ? (
                          <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.45)" }}>
                            {t("opsmate.suggestion.buildingReferences")}
                          </span>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {streamingDocs.map((doc: any, idx: number) => {
                              const docName = typeof doc === "string" ? doc : doc.name;
                              const segNums: number[] =
                                typeof doc === "object" && Array.isArray(doc.segmentNums)
                                  ? doc.segmentNums
                                  : [];
                              return (
                                <div
                                  key={idx}
                                  style={{
                                    color: "rgba(0, 0, 0, 0.65)",
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    aria-hidden
                                    style={{
                                      width: 4,
                                      height: 4,
                                      borderRadius: "50%",
                                      background: "rgba(0, 0, 0, 0.45)",
                                      flex: "0 0 auto",
                                    }}
                                  />
                                  <span>{docName}</span>
                                  {segNums.map((n) => (
                                    <Tag key={n} variant="filled" style={CITATION_TAG_STYLE}>
                                      {n}
                                    </Tag>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            );
          }

          if (currentMessage) {
            return renderThreePartContent(currentMessage);
          }
          // 兜底
          return highlightCitations(
            typeof content === "string" ? content : "",
            (currentMessage as any)?.documents,
          );
        },
        footer: (content: any, { key, extraInfo }: any) => {
          // 错误状态不显示 footer
          const msg = itemsRef.current.find((item) => item.key === key);
          if (
            content ===
              "The knowledge base is currently being updated. Some documents have not yet been fully parsed, so a reliable answer cannot be generated at this time. Please try again later." ||
            content === "Okay, I've stopped responding." ||
            /^\[请求失败\s\d+\]/.test(content) ||
            (!content && msg?.status === "streaming")
          ) {
            return null;
          }
          if (msg?.status === "streaming") return null;
          if (msg?.status === "error") return null;

          const feedbackValue =
            (extraInfo?.feedback as "like" | "dislike" | "default") || "default";
          const isTranslated = extraInfo != null && "isTranslated" in extraInfo;
          const sessionLogId = (extraInfo as any)?.sessionLogId
            ? String((extraInfo as any).sessionLogId)
            : typeof key === "string" && key.length >= 36
              ? key.substring(0, 36)
              : "";
          const messageKey = String(key ?? "");
          const copying = Boolean(copyingMap[messageKey]);
          const handleCopy = async () => {
            if (copying) return;
            setCopyingMap((prev) => ({ ...prev, [messageKey]: true }));

            const original = typeof content === "string" ? content : String(content ?? "");
            const translatedText = (extraInfo as any)?.translatedText;

            // ── 清洗角标：[N] 和 <pike_rag_cite>N</pike_rag_cite> ──
            const cleanCitations = (text: string) =>
              text
                .replace(/\[\d+(?:-\d+)?\]/g, "") // 去掉 [1]、[2-3]
                .replace(/<\|?pike_rag_cite\|?>\s*\d+(?:-\d+)?\s*<\|?\/pike_rag_cite\|?>/gi, "") // 去掉 XML 格式
                .replace(/\s+/g, " ") // 可选：清理残留的多余空格
                .trim();

            const text =
              typeof translatedText === "string" && translatedText.length > 0
                ? `${cleanCitations(original)}\n\n-------\n\n${cleanCitations(translatedText)}`
                : cleanCitations(original);

            const answerNodes = Array.from(
              document.querySelectorAll<HTMLElement>("[data-copy-answer]"),
            ).filter((node) => node.getAttribute("data-copy-answer") === messageKey);

            // 复制前对 DOM 副本做清洗：
            //   - 去掉标题，避免复制结果重复显示 "Answer"
            //   - 去掉引用角标（data-pike-citation 的 Tag），避免把 [1][2] 这类编号带进剪贴板
            const cleanedNodes = answerNodes.map((node) => {
              const cloned = node.cloneNode(true) as HTMLElement;
              cloned.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((el) => el.remove());
              cloned.querySelectorAll("[data-pike-citation]").forEach((el) => el.remove());
              return cloned;
            });

            const plainFromDom = cleanedNodes
              .map((node) => node.innerText.trim().replace(/\[\d+(?:-\d+)?\]/g, ""))
              .filter(Boolean)
              .join("\n\n-------\n\n");

            const htmlFromDom = cleanedNodes
              .map((node) => `<div>${node.innerHTML}</div>`)
              .join('<hr style="border:none;border-top:1px solid #d9d9d9;margin:16px 0;" />');

            const plainText = plainFromDom || text;
            const htmlText = htmlFromDom
              ? `<!doctype html><html><body>${htmlFromDom}</body></html>`
              : "";

            try {
              if (
                navigator.clipboard &&
                window.isSecureContext &&
                typeof navigator.clipboard.write === "function" &&
                typeof ClipboardItem !== "undefined" &&
                htmlText
              ) {
                const item = new ClipboardItem({
                  "text/plain": new Blob([plainText], { type: "text/plain" }),
                  "text/html": new Blob([htmlText], { type: "text/html" }),
                });
                await navigator.clipboard.write([item]);
              } else if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(plainText);
              } else {
                const textarea = document.createElement("textarea");
                textarea.value = plainText;
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
              }
              messageRef.current.success("Copied");
            } catch {
              messageRef.current.error("Copy failed");
            } finally {
              window.setTimeout(() => {
                setCopyingMap((prev) => ({ ...prev, [messageKey]: false }));
              }, 3000);
            }
          };

          const handleFeedbackClick = async (next: "like" | "dislike") => {
            if (!sessionLogId) {
              messageRef.current.warning("未获取到当前答案的 SessionLogId，无法点赞/点踩");
              return;
            }
            // 点踩：先弹二次确认弹窗
            if (next === "dislike") {
              if (feedbackValue === "dislike") {
                handleFeedbackChange(String(key ?? ""), "default", sessionLogId);
                return;
              } else {
                setDislikeConfirmTarget({
                  sessionLogId,
                  messageKey: String(key ?? ""),
                });
                setDislikeConfirmVisible(true);
                return;
              }
            }

            handleFeedbackChange(String(key ?? ""), next, sessionLogId);
          };

          const handleTranslateClick = () => {
            if (!sessionLogId) {
              messageRef.current.warning("未获取到当前答案的 SessionLogId，无法翻译");
              return;
            }
            handleTranslate(String(key ?? ""), sessionLogId);
          };

          return (
            <Actions
              items={
                [
                  {
                    key: "copy",
                    label: "copy",
                    actionRender: () => (
                      <Tooltip title="Copy">
                        <button
                          type="button"
                          onClick={handleCopy}
                          style={{
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {copying ? (
                            <CheckOutlined style={{ color: "#666666" }} />
                          ) : (
                            <img src={CopyIcon} alt="Copy" />
                          )}
                        </button>
                      </Tooltip>
                    ),
                  },
                  hideTranslateAndFeedback
                    ? null
                    : {
                        key: "translate",
                        label: "translate",
                        actionRender: () => (
                          <Tooltip title="Translate">
                            <button
                              type="button"
                              onClick={handleTranslateClick}
                              style={{
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={isTranslated ? TranslationedIcon : TranslationIcon}
                                alt="Translate"
                              />
                            </button>
                          </Tooltip>
                        ),
                      },
                  hideTranslateAndFeedback
                    ? null
                    : {
                        key: "feedback",
                        actionRender: () => (
                          <div
                            style={{
                              display: "inline-flex",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            <Tooltip title="Good response">
                              <button
                                type="button"
                                onClick={() => handleFeedbackClick("like")}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  padding: 0,
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={
                                    feedbackValue === "like"
                                      ? GoodResponseActiveIcon
                                      : GoodResponseIcon
                                  }
                                  alt="Good response"
                                />
                              </button>
                            </Tooltip>
                            <Tooltip title="Bad response">
                              <button
                                type="button"
                                onClick={() => handleFeedbackClick("dislike")}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  padding: 0,
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={
                                    feedbackValue === "dislike"
                                      ? BadResponseActiveIcon
                                      : BadResponseIcon
                                  }
                                  alt="Bad response"
                                />
                              </button>
                            </Tooltip>
                          </div>
                        ),
                      },
                  // AI Reasoning（Rationale）入口已隐藏：推理过程改由 ProgressPanel 展示
                  // {
                  //   key: "aireasoning",
                  //   label: "reasoning",
                  //   actionRender: () => {
                  //     const msg = itemsRef.current.find((item) => item.key === key);
                  //     if (!msg) return null;
                  //     const translatedRationale = msg?.extraInfo?.translatedRationale;
                  //     const originalRationale = msg?.rationale ?? "";
                  //     const rationale =
                  //       typeof translatedRationale === "string" && translatedRationale.length > 0
                  //         ? `${originalRationale}\n\n-------\n\n${translatedRationale}`
                  //         : originalRationale;
                  //     return (
                  //       <Tooltip title="AI Reasoning">
                  //         <Button
                  //           onClick={() => {
                  //             setAIReasoningVisible(true);
                  //             setAIReasoning(rationale);
                  //           }}
                  //           type="text"
                  //           size="small"
                  //           icon={<img src={AIReasoningIcon} alt="AI Reasoning" />}
                  //         />
                  //       </Tooltip>
                  //     );
                  //   },
                  // },
                  // {
                  //   key: 'regenerate',
                  //   label: 'regenerate',
                  //   actionRender: () => {
                  //     const msg = itemsRef.current.find((item) => item.key === key);
                  //     if (!msg || msg.status === 'loading') return null;
                  //     return (
                  //       <Tooltip title="Regenerate">
                  //         <Button
                  //           onClick={() => regenerateMessage(String(key ?? ''))}
                  //           type="text"
                  //           size="small"
                  //           icon={<ReloadOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 16 }} />}
                  //         />
                  //       </Tooltip>
                  //     );
                  //   },
                  // },
                ].filter(Boolean) as any
              }
            />
          );
        },
      },
      user: {
        placement: "end" as const,
        className: "user-message",
      },
      system: {
        placement: "start" as const,
      },
    }),
    [
      renderThreePartContent,
      renderMarkdownAnswer,
      highlightCitations,
      handleTranslate,
      handleFeedbackChange,
      regenerateMessage,
      itemsRef,
      t,
      setDislikeConfirmTarget,
      setDislikeConfirmVisible,
      copyingMap,
      hideTranslateAndFeedback,
    ],
  );

  return {
    roles,
    highlightCitations,
    renderMarkdownAnswer,
    renderThreePartContent,
    canShowDownload,
    getDocUrl,
  };
}
